import { supabase } from '../../config/supabase';

/**
 * Update a job/assignment
 * @param {string} jobId - The job ID (UUID or unique_id)
 * @param {Object} updateData - Data to update
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Updated job
 */
export const updateAssignment = async (jobId, updateData, companyId) => {
    try {
        const { data, error } = await supabase
            .from('jobs')
            .update(updateData)
            .eq('id', jobId)
            .eq('company_id', companyId)
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error updating assignment:', error);
        throw error;
    }
};

/**
 * Update job quotes
 * @param {string} jobId - The job ID
 * @param {Object} quotes - Object with quote types and their data
 * @returns {Promise<Array>} Updated quotes
 */
export const updateJobQuotes = async (jobId, quotes) => {
    try {
        const updates = [];

        for (const [quoteType, quoteData] of Object.entries(quotes)) {
            // Check if record exists (parent-level quote only — no child_unique_id)
            const { data: existingQuote, error: fetchError } = await supabase
                .from('job_quotes')
                .select('id')
                .eq('job_id', jobId)
                .eq('quote_type', quoteType)
                .is('child_unique_id', null)
                .or('delivery_count.is.null,delivery_count.eq.0')
                .maybeSingle();

            if (fetchError) throw fetchError;

            let data, error;

            if (existingQuote) {
                // Update existing quote
                const result = await supabase
                    .from('job_quotes')
                    .update({
                        is_required: quoteData.isRequired,
                        // expected_delivery_date: quoteData?.expectedDeliveryDate || null,
                        // invoiced: quoteData.invoiced || false,
                        // expected_finish_date: quoteData?.expectedDeliveryDate || null,
                    })
                    .eq('id', existingQuote.id)
                    .select()
                    .single();

                data = result.data;
                error = result.error;
            } else {
                // Insert new quote
                const result = await supabase
                    .from('job_quotes')
                    .insert({
                        job_id: jobId,
                        quote_type: quoteType,
                        is_required: quoteData.isRequired,
                        // expected_delivery_date: quoteData?.expectedDeliveryDate || null,
                        // invoiced: quoteData.invoiced || false,
                        // expected_finish_date: quoteData?.expectedDeliveryDate || null,
                    })
                    .select()
                    .single();

                data = result.data;
                error = result.error;
            }

            if (error) throw error;
            updates.push(data);
        }

        return updates;
    } catch (error) {
        console.error('Error updating job quotes:', error);
        throw error;
    }
};

/**
 * Update dates on all child quote rows (new-system jobs where is_parent = true).
 * Applies the "sync" rule: if only one date is provided, both columns get that value.
 * Runs a single UPDATE per quote type so all children of that type are updated at once.
 *
 * @param {string} jobId - The job ID
 * @param {Object} quotes - formData.quotes: { roof: { expectedFinishDate, expectedDeliveryDate }, ... }
 */
export const updateChildQuotesDates = async (jobId, quotes) => {
    try {
        for (const [quoteType, quoteData] of Object.entries(quotes)) {
            const rawFinish = quoteData?.expectedFinishDate || null;
            const rawDelivery = quoteData?.expectedDeliveryDate || null;

            // Sync: if only one is provided, the other mirrors it
            const finishDate = rawFinish || rawDelivery || null;
            const deliveryDate = rawDelivery || null;

            const { error } = await supabase
                .from('job_quotes')
                .update({
                    expected_finish_date: deliveryDate,
                    expected_delivery_date: deliveryDate,
                })
                .eq('job_id', jobId)
                .eq('quote_type', quoteType)
                .not('child_unique_id', 'is', null)
                .or('delivery_count.is.null,delivery_count.eq.0');

            if (error) throw error;
        }
    } catch (error) {
        console.error('Error updating child quote dates:', error);
        throw error;
    }
};

/**
 * Update both date columns on a single job_quotes row by ID.
 */
export const updateSingleQuoteDate = async (quoteId, date) => {
    const { error } = await supabase
        .from('job_quotes')
        .update({
            expected_finish_date: date || null,
            expected_delivery_date: date || null,
            delivery_playground: false,
            quote_playground: false
        })
        .eq('id', quoteId);
    if (error) throw error;
};

/**
 * Update the invoice_state on a single job_quotes row by ID.
 */
export const updateQuoteInvoiceState = async (quoteId, invoiceState) => {
    const { error } = await supabase
        .from('job_quotes')
        .update({ invoice_state: invoiceState })
        .eq('id', quoteId);
    if (error) throw error;
};

/**
 * Toggle the invoiced boolean on a single job_quotes row by ID.
 */
export const updateQuoteInvoiced = async (quoteId, invoiced) => {
    const { error } = await supabase
        .from('job_quotes')
        .update({ invoiced })
        .eq('id', quoteId);
    if (error) throw error;
};

export const updateQuoteDeliveryField = async (quoteId, field, value) => {
    const { error } = await supabase
        .from('job_quotes')
        .update({ [field]: value })
        .eq('id', quoteId);
    if (error) throw error;
};

/**
 * Update job floors
 * @param {string} jobId - The job ID
 * @param {Array} floors - Array of floor data
 * @returns {Promise<Array>} Updated floors
 */
export const updateJobFloors = async (jobId, floors) => {
    try {
        // First, delete existing floors
        const { error: deleteError } = await supabase
            .from('job_floors')
            .delete()
            .eq('job_id', jobId);

        if (deleteError) throw deleteError;

        // Then insert new floors
        const { data, error } = await supabase
            .from('job_floors')
            .insert(
                floors.map(floor => ({
                    job_id: jobId,
                    floor_number: floor.floorNumber,
                    floor_truss_size: floor.floorTrussSize || null,
                    flooring: floor.flooring || null,
                    balcony_included: floor.balconyIncluded
                }))
            )
            .select();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error updating job floors:', error);
        throw error;
    }
};

/**
 * Update job floor specs
 * @param {string} jobId - The job ID
 * @param {Object} floorSpecs - Floor specifications
 * @returns {Promise<Array>} Updated floor specs
 */
export const updateJobFloorSpecs = async (jobId, floorSpecs) => {
    try {
        const updates = [];

        // Delete existing specs first
        const { error: deleteError } = await supabase
            .from('job_floor_specs')
            .delete()
            .eq('job_id', jobId);

        if (deleteError) throw deleteError;

        // Insert new specs based on storey type
        if (floorSpecs.isSingleStorey) {
            const { data, error } = await supabase
                .from('job_floor_specs')
                .insert({
                    job_id: jobId,
                    floor_level: 'single_storey',
                    non_load_bearing_wall_stud_sizes: floorSpecs.singleStorey.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: floorSpecs.singleStorey.nonLoadBearingWallStudCentres || null,
                    wall_height: floorSpecs.singleStorey.wallHeight || null
                })
                .select()
                .single();

            if (error) throw error;
            updates.push(data);
        } else {
            // Ground floor
            const { data: groundData, error: groundError } = await supabase
                .from('job_floor_specs')
                .insert({
                    job_id: jobId,
                    floor_level: 'ground',
                    non_load_bearing_wall_stud_sizes: floorSpecs.doubleStorey.groundFloor.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: floorSpecs.doubleStorey.groundFloor.nonLoadBearingWallStudCentres || null,
                    wall_height: floorSpecs.doubleStorey.groundFloor.wallHeight || null
                })
                .select()
                .single();

            if (groundError) throw groundError;
            updates.push(groundData);

            // First floor
            const { data: firstData, error: firstError } = await supabase
                .from('job_floor_specs')
                .insert({
                    job_id: jobId,
                    floor_level: 'first',
                    non_load_bearing_wall_stud_sizes: floorSpecs.doubleStorey.firstFloor.nonLoadBearingWallStudSizes || null,
                    non_load_bearing_wall_stud_centres: floorSpecs.doubleStorey.firstFloor.nonLoadBearingWallStudCentres || null,
                    wall_height: floorSpecs.doubleStorey.firstFloor.wallHeight || null
                })
                .select()
                .single();

            if (firstError) throw firstError;
            updates.push(firstData);
        }

        return updates;
    } catch (error) {
        console.error('Error updating floor specs:', error);
        throw error;
    }
};

export const deleteJobFiles = async (fileIds, jobId, jobName, user) => {
    try {

        let allFilenames = ``;

        for (const fileId of fileIds) {
            // Get file info first
            const { data: fileData, error: fetchError } = await supabase
                .from('job_files')
                .select('filename')
                .eq('id', fileId)
                .single();

            if (fetchError) {
                console.error('Error fetching file:', fetchError);
                continue;
            }

            if (fileData && fileData.filename) {

                allFilenames += `${fileData.filename}, `

                // Delete from job_files bucket (with underscore)
                const { error: storageError } = await supabase.storage
                    .from('job-files')
                    .remove([fileData.filename]);


                if (storageError) {
                    console.error('Error deleting from storage:', storageError);
                }

                // Delete from database
                const { error: dbError } = await supabase
                    .from('job_files')
                    .delete()
                    .eq('id', fileId);

                if (dbError) {
                    console.error('Error deleting from database:', dbError);
                }
            }
        }

        if (fileIds?.length > 0) {
            // 6. Create action log
            const { error: logError } = await supabase
                .from('job_action_logs')
                .insert({
                    job_id: jobId,
                    user_id: user?.id || null,
                    user_name: user?.full_name || user?.username,
                    user_role: user?.role || null,
                    action: 'files_deleted',
                    details: `Files: ${allFilenames} deleted for job ${jobName}`
                });

            if (logError) throw logError;
        }

    } catch (error) {
        console.error('Error deleting files:', error);
        throw error;
    }
};

/**
 * Mirror the DB trigger logic on the JS side for job edits.
 *
 * The trigger only fires on INSERT (job creation). When a user edits a job
 * and changes storey, unit count, or enabled departments, this function
 * creates any missing job_quotes rows using the same naming convention as the
 * trigger.
 *
 * Uses a diff-based approach: generates the full expected set of child IDs,
 * fetches all existing child IDs, and inserts only the ones that are missing.
 *
 * Only runs for "new-system" jobs where selected_departments is an actual
 * array (even if empty). Old jobs where selected_departments is NULL are
 * left untouched.
 *
 * @param {Object}   job            - The full originalAssignment object
 * @param {string[]} requiredDepts  - Department types currently checked as required (e.g. ['roof','wall'])
 * @param {string}   currentJobType - Current job_type value from the form
 * @param {string}   currentStorey  - Current storey value derived from form ('single'|'double'|'triple')
 * @param {number}   currentUnits   - Current unit count derived from form (1 if not a unit job)
 */
export const syncJobQuotesAfterEdit = async (
    job,
    requiredDepts,
    currentJobType,
    currentStorey,
    currentUnits
) => {
    if (!job?.is_parent) return;
    if (job.selected_departments?.length <= 0) return;

    const units = currentUnits || 1;
    const storey = currentStorey || 'single';
    const uid = job.unique_id;
    const jt = currentJobType || job.job_type;

    const vDesignStatus =
        ['Design', 'Design & Estimation'].includes(jt)
            ? 'waiting_assignment'
            : 'not_required';

    const vEstimationStatus =
        ['Estimation', 'Design & Estimation'].includes(jt)
            ? 'waiting_assignment'
            : 'not_required';

    // -----------------------------
    // 1️⃣ Build expected rows
    // -----------------------------
    const expectedRows = [];

    for (const dept of requiredDepts) {
        for (let u = 1; u <= units; u++) {
            const sfx = units > 1 ? `-${u}` : '';

            const base = {
                job_id: job.id,
                is_required: true,
                design_status: vDesignStatus,
                estimation_status: vEstimationStatus,
            };

            if (dept === 'roof') {
                expectedRows.push({ ...base, quote_type: 'roof', child_unique_id: `${uid}R${sfx}` });
            }

            if (dept === 'wall') {
                expectedRows.push({ ...base, quote_type: 'wall', child_unique_id: `${uid}W${sfx}` });

                if (storey === 'double' || storey === 'triple') {
                    expectedRows.push({ ...base, quote_type: 'wall', child_unique_id: `${uid}WL1${sfx}` });
                }

                if (storey === 'triple') {
                    expectedRows.push({ ...base, quote_type: 'wall', child_unique_id: `${uid}WL2${sfx}` });
                }
            }

            if (dept === 'floor') {
                expectedRows.push({ ...base, quote_type: 'floor', child_unique_id: `${uid}F${sfx}` });

                if (storey === 'triple') {
                    expectedRows.push({ ...base, quote_type: 'floor', child_unique_id: `${uid}FL1${sfx}` });
                }
            }
        }
    }

    // -----------------------------
    // 2️⃣ Fetch existing quotes
    // -----------------------------
    const { data: existing, error } = await supabase
        .from('job_quotes')
        .select('*')
        .eq('job_id', job.id)
        .not('child_unique_id', 'is', null);

    if (error) throw error;

    const expectedMap = new Map(
        expectedRows.map(r => [r.child_unique_id, r])
    );

    const existingMap = new Map(
        (existing || []).map(r => [r.child_unique_id, r])
    );

    const toInsert = [];
    const toUpdate = [];

    // -----------------------------
    // 3️⃣ Handle INSERT + UPDATE
    // -----------------------------
    for (const [id, expectedRow] of expectedMap.entries()) {
        if (existingMap.has(id)) continue; // ⬅️ THIS is the key line

        toInsert.push(expectedRow);
    }
    // for (const [id, expectedRow] of expectedMap.entries()) {
    //     const existingRow = existingMap.get(id);

    //     if (!existingRow) {
    //         // Brand-new child — insert with fresh statuses
    //         toInsert.push(expectedRow);
    //     } else if (existingRow.is_required === false) {
    //         // Previously disabled — re-enable only; preserve all progress/statuses
    //         toUpdate.push({
    //             id: existingRow.id,
    //             is_required: true,
    //         });
    //     } else {
    //         // Already active — only sync non-status fields (e.g. finish date)
    //         const needsUpdate =
    //             existingRow.expected_finish_date !== expectedRow.expected_finish_date;

    //         if (needsUpdate) {
    //             toUpdate.push({
    //                 id: existingRow.id,
    //                 // expected_finish_date: expectedRow.expected_finish_date,
    //             });
    //         }
    //     }
    // }

    // -----------------------------
    // 4️⃣ Soft-disable removed quotes
    // -----------------------------
    // for (const [id, existingRow] of existingMap.entries()) {
    //     if (!expectedMap.has(id) && existingRow.is_required) {
    //         toUpdate.push({
    //             id: existingRow.id,
    //             is_required: false,
    //         });
    //     }
    // }

    // -----------------------------
    // 5️⃣ Execute DB changes
    // -----------------------------
    if (toInsert.length > 0) {
        const { error: insertErr } = await supabase
            .from('job_quotes')
            .insert(toInsert);

        if (insertErr) throw insertErr;
    }

    for (const updateRow of toUpdate) {
        const { id, ...fields } = updateRow;

        const { error: updateErr } = await supabase
            .from('job_quotes')
            .update(fields)
            .eq('id', id);

        if (updateErr) throw updateErr;
    }
};

/**
 * Create a new child quote row for a job
 */
export const createChildQuote = async (jobId, quoteData) => {
    try {
        const { data, error } = await supabase
            .from('job_quotes')
            .insert({
                job_id: jobId,
                quote_type: quoteData.quoteType,
                is_required: true,
                child_unique_id: quoteData.childUniqueId,
                expected_finish_date: quoteData.expectedDeliveryDate || null,
                expected_delivery_date: quoteData.expectedDeliveryDate || null,
                invoiced: false,
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error creating child quote:', error);
        throw error;
    }
};

/**
 * Set is_required on a specific job_quotes row
 */
export const setChildQuoteRequired = async (quoteId, isRequired) => {
    try {
        const { data, error } = await supabase
            .from('job_quotes')
            .update({ is_required: isRequired })
            .eq('id', quoteId)
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error updating child quote required status:', error);
        throw error;
    }
};

/**
 * Log job action
 * @param {string} jobId - The job ID
 * @param {string} userId - User ID
 * @param {string} userName - User name
 * @param {string} userRole - User role
 * @param {string} action - Action description
 * @param {Object} details - Additional details
 * @returns {Promise<Object>} Created log entry
 */
export const logJobAction = async (jobId, userId, userName, userRole, action, details = {}) => {
    try {
        const { data, error } = await supabase
            .from('job_action_logs')
            .insert({
                job_id: jobId,
                user_id: userId,
                user_name: userName,
                user_role: userRole,
                action: action,
                details: typeof details === 'string' ? details : JSON.stringify(details)
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error logging action:', error);
        throw error;
    }
};

export const isChildUniqueIdTaken = async (jobId, childUniqueId, excludeQuoteId = null) => {
    let query = supabase
        .from('job_quotes')
        .select('id')
        .ilike('child_unique_id', childUniqueId);

    if (excludeQuoteId) {
        query = query.neq('id', excludeQuoteId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []).length > 0;
};

export const updateChildUniqueId = async (quoteId, childUniqueId) => {
    const { error } = await supabase
        .from('job_quotes')
        .update({ child_unique_id: childUniqueId })
        .eq('id', quoteId);
    if (error) throw error;
};
